// ─── client-placed driver: echo ──────────────────────────────────────────────
// The `echo` provider supports client placement too, and this is its client half:
// no model, no network, no keys. It exists so the whole client-placed code path —
// transcripts reported up, tools round-tripped, audio kept local — can be exercised
// end to end without LM Studio running. Same directives as the server-side echo:
// `/tool <name> [json]` calls a tool.

import type { ClientProviderConfig, Role } from '@shared/protocol.ts'
import type { ClientDriver, DriverHost, DriverOptions } from './types.ts'

const TOOL_DIRECTIVE = /^\/tool\s+([a-zA-Z0-9_-]+)\s*(.*)$/s

export function createEcho(_config: ClientProviderConfig, host: DriverHost, opts: DriverOptions = {}): ClientDriver {
  let heard = 0

  const reply = (text: string) => {
    host.emit({ t: 'transcript', role: 'assistant', text, final: true })
    // Stands in for TTS, exactly as the server-side echo does: the text's own bytes.
    host.play(new TextEncoder().encode(text).buffer as ArrayBuffer)
  }

  const respond = async (text: string) => {
    const directive = TOOL_DIRECTIVE.exec(text)
    if (!directive) return reply(`echo: ${text}`)
    const outcome = await host.runTool(directive[1], parseArgs(directive[2]))
    reply(outcome.ok ? `tool ${directive[1]} said ${JSON.stringify(outcome.result)}` : `tool ${directive[1]} failed: ${outcome.error}`)
  }

  return {
    id: 'echo',
    async start() {
      if (opts.greeting) reply(opts.greeting)
    },
    pushAudio(pcm) {
      heard += pcm.byteLength
    },
    commit() {
      const bytes = heard
      heard = 0
      host.emit({ t: 'transcript', role: 'user', text: `[${bytes} bytes of audio]`, final: true })
      reply(`heard ${bytes} bytes`)
    },
    say(text: string, role: Role) {
      if (role === 'assistant') return reply(text)
      if (role === 'user') void respond(text)
    },
    cancel() {
      heard = 0
    },
    close() {},
  }
}

function parseArgs(raw: string): unknown {
  const trimmed = raw.trim()
  if (!trimmed) return {}
  try {
    return JSON.parse(trimmed)
  } catch {
    return { _raw: trimmed }
  }
}
